import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import type { ProdutoCaixa, PromocaoCategoriaGrupoCaixa, ProdutoCategoriaPromo } from '@caixa/lib/api';
import { formatBRL } from '@caixa/lib/format';

export type ItemEscolhidoPromo = {
  produto_id: number;
  nome_produto: string;
  quantidade: number;
  preco_unitario: number;
};

type Props = {
  open: boolean;
  produto: ProdutoCaixa | null;
  onClose: () => void;
  onConfirm: (itens: ItemEscolhidoPromo[]) => void;
};

function emptySlots(grupos: PromocaoCategoriaGrupoCaixa[]): number[][] {
  return grupos.map((g) => Array(Math.max(1, g.quantidade)).fill(0));
}

export function PromoCategoriaModal({ open, produto, onClose, onConfirm }: Props) {
  const grupos = produto?.promocao_categorias_grupos ?? [];
  const [slots, setSlots] = useState<number[][]>(() => emptySlots(grupos));

  useEffect(() => {
    if (open && produto) {
      setSlots(emptySlots(produto.promocao_categorias_grupos ?? []));
    }
  }, [open, produto]);

  const completo =
    grupos.length > 0 &&
    grupos.every((g, gi) => {
      const row = slots[gi];
      if (!row || row.length !== g.quantidade) return false;
      return row.every((id) => id > 0);
    });

  const setSlot = (gi: number, si: number, produtoId: number) => {
    setSlots((prev) => {
      const next = prev.map((r) => [...r]);
      if (!next[gi]) return prev;
      next[gi] = [...next[gi]];
      next[gi][si] = produtoId;
      return next;
    });
  };

  const handleConfirm = () => {
    if (!produto || !completo) return;
    const itens: ItemEscolhidoPromo[] = [];
    grupos.forEach((g, gi) => {
      const row = slots[gi] ?? [];
      row.forEach((pid) => {
        const p = g.produtos.find((x) => x.id === pid);
        if (p) {
          itens.push({
            produto_id: pid,
            nome_produto: p.nome,
            quantidade: 1,
            preco_unitario: p.preco_venda ?? 0,
          });
        }
      });
    });
    if (itens.length === 0) return;
    onConfirm(itens);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="caixa-overlay">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="promo-cat-title"
        className="caixa-modal caixa-modal--lg"
      >
        <div className="caixa-modal-head">
          <h2 id="promo-cat-title" className="caixa-modal-title">
            {produto?.nome ?? 'Promoção'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="caixa-modal-close"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {produto && (
          <p className="text-amber-400 font-medium mb-4">{formatBRL(produto.preco_venda ?? 0)}</p>
        )}
        <p className="caixa-modal-sub">Escolha os produtos de cada categoria conforme a quantidade da promoção.</p>
        <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
          {grupos.map((g, gi) => (
            <div key={`${g.categoria}-${gi}`} className="border border-[#3a3a3a] rounded-lg p-4 bg-[#242424]">
              <p className="text-sm font-semibold text-[#f5f5f5] mb-3">{g.categoria}</p>
              <div className="space-y-2">
                {Array.from({ length: g.quantidade }).map((_, si) => (
                  <div key={si} className="caixa-field" style={{ marginBottom: 0 }}>
                    <label htmlFor={`promo-cat-${gi}-${si}`}>Item {si + 1}</label>
                    <select
                      id={`promo-cat-${gi}-${si}`}
                      className="caixa-input"
                      value={slots[gi]?.[si] ?? 0}
                      onChange={(e) => setSlot(gi, si, parseInt(e.target.value, 10) || 0)}
                    >
                      <option value={0}>Selecione o produto…</option>
                      {g.produtos.map((p: ProdutoCategoriaPromo) => (
                        <option key={p.id} value={p.id}>
                          {p.nome}
                          {p.preco_venda != null ? ` — ${formatBRL(p.preco_venda)}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="caixa-modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="caixa-btn-outline"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!completo || grupos.length === 0}
            className="caixa-btn-success"
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}
